/**
 * Formatting for the countdown. Separated from legs.ts so the pace math stays
 * numbers-only, and kept out of the components so the strings are testable.
 */

import type { ISODate } from "@schema/catalog.ts";

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = 3_600_000;
const MS_PER_DAY = 86_400_000;

const pad = (value: number): string => String(value).padStart(2, "0");

/**
 * The `hh:mm:ss` remainder *within* the final day — the part that visibly
 * moves. Days are rendered separately and much bigger; this is the detail that
 * proves the number above it is live.
 */
export function formatClock(msLeft: number): string {
  const withinDay = Math.max(0, msLeft) % MS_PER_DAY;
  const hours = Math.floor(withinDay / MS_PER_HOUR);
  const minutes = Math.floor((withinDay % MS_PER_HOUR) / MS_PER_MINUTE);
  const seconds = Math.floor((withinDay % MS_PER_MINUTE) / MS_PER_SECOND);
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

/**
 * Hours per day, at the precision the number deserves.
 *
 * Under one hour it takes two decimals, and that is not fussiness: a leg
 * needing 0.90 against a budget of 0.86 is a leg that doesn't fit, and at one
 * decimal both read "0.9" and the callout looks like it's lying. Above ten
 * hours the decimal is noise.
 */
export function formatHoursPerDay(hours: number): string {
  if (!Number.isFinite(hours)) return "∞";
  if (hours === 0) return "0";
  if (hours < 1) return hours.toFixed(2);
  if (hours < 10) return hours.toFixed(1);
  return String(Math.round(hours));
}

/** Whole hours, rounded, for runtime totals in prose ("31 hours left"). */
export function formatHours(minutes: number): string {
  return String(Math.round(minutes / 60));
}

/** Half-hour granularity, trailing `.0` dropped — how the pace remedy asks. */
export function formatHoursPerWeek(hours: number): string {
  return Number.isInteger(hours) ? String(hours) : hours.toFixed(1);
}

/** "Sep 25, 2026" — short enough to sit under a big number. */
export function formatDeadline(date: ISODate): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** "51 days" / "1 day" / "today". */
export function formatDays(days: number): string {
  if (days === 0) return "today";
  return `${days} ${days === 1 ? "day" : "days"}`;
}
